import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { authAPI } from '../utils/api';
import { OffertifyLogo } from '../components/OffertifyLogo';

type ConfirmState = 'verifying' | 'success' | 'error' | 'missing_token';

const ConfirmEmailChange: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState<ConfirmState>(token ? 'verifying' : 'missing_token');
  // Guard against React StrictMode double-invoking the effect: the token is
  // single-use, so the confirm call must fire exactly once per mount.
  const requestedRef = useRef(false);

  useEffect(() => {
    if (!token || requestedRef.current) return;
    requestedRef.current = true;

    authAPI
      .confirmEmailChange({ token })
      .then(() => setState('success'))
      .catch(() => setState('error'));
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="mx-auto h-20 w-20 flex items-center justify-center rounded-2xl bg-primary-50 shadow-sm border border-primary-100">
            <OffertifyLogo className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="mt-6 text-3xl font-bold tracking-tight text-secondary-900">
            Conferma cambio email
          </h2>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-secondary-200 p-8">
          <div aria-live="polite">
            {state === 'verifying' && (
              <div className="flex items-center justify-center gap-3 text-secondary-700">
                <svg
                  className="animate-spin h-5 w-5 text-primary-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-sm">Verifica del link in corso...</p>
              </div>
            )}

            {state === 'success' && (
              <div className="text-center space-y-3">
                <CheckCircle className="mx-auto h-10 w-10 text-green-500" />
                <p className="text-sm font-medium text-secondary-900">
                  Indirizzo email aggiornato.
                </p>
                <p className="text-sm text-secondary-600">
                  D'ora in poi userai il nuovo indirizzo per accedere. La tua sessione attuale
                  resta attiva.
                </p>
                <Link
                  to="/my-profile"
                  className="inline-block mt-2 font-semibold text-primary-600 hover:text-primary-700"
                >
                  Vai al profilo
                </Link>
              </div>
            )}

            {state === 'error' && (
              <div className="text-center space-y-3" role="alert">
                <XCircle className="mx-auto h-10 w-10 text-red-500" />
                <p className="text-sm font-medium text-secondary-900">
                  Link scaduto o non valido.
                </p>
                <p className="text-sm text-secondary-600">
                  Il link potrebbe essere scaduto, già utilizzato o il nuovo indirizzo non è più
                  disponibile. Richiedi un nuovo cambio email dal tuo profilo.
                </p>
                <Link
                  to="/my-profile"
                  className="inline-block mt-2 font-semibold text-primary-600 hover:text-primary-700"
                >
                  Vai al profilo
                </Link>
              </div>
            )}

            {state === 'missing_token' && (
              <div className="text-center space-y-3" role="alert">
                <XCircle className="mx-auto h-10 w-10 text-red-500" />
                <p className="text-sm font-medium text-secondary-900">
                  Link di conferma non valido o incompleto.
                </p>
                <p className="text-sm text-secondary-600">
                  Apri il link ricevuto via email oppure richiedi un nuovo cambio email dal tuo
                  profilo.
                </p>
              </div>
            )}
          </div>
        </div>

        <p className="mt-8 text-center text-sm text-secondary-600">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 font-semibold text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Torna al login
          </Link>
        </p>
      </div>
    </div>
  );
};

export default ConfirmEmailChange;
